import { type SignupInput,signupSchema } from '@emailforge/schemas';
import { Button, Input } from '@emailforge/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { setSession } from '@/features/auth/authSlice';
import { useAppDispatch } from '@/store/hooks';

import { AuthBrandSide } from './AuthBrandSide';
import { OAuthButtons } from './OAuthButtons';

export function Signup() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // When the API replies "account already exists" we surface a magic-link CTA
  // instead of just an error message — the email exists (e.g. created via a
  // prior magic-link request) and the right move is to sign them in, not
  // re-prompt for password creation.
  const [existingAccountEmail, setExistingAccountEmail] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: { acceptTerms: undefined as unknown as true },
  });

  const password = watch('password') ?? '';
  const strength = passwordStrength(password);

  const onSubmit = async (data: SignupInput) => {
    setSubmitError(null);
    setExistingAccountEmail(null);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        const msg = body.message ?? `Signup failed (${res.status})`;
        if (/already exists/i.test(msg)) {
          setExistingAccountEmail(data.email);
          return;
        }
        throw new Error(msg);
      }
      const { accessToken, refreshToken } = (await res.json()) as {
        accessToken: string;
        refreshToken?: string;
      };
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        dispatch(setSession({ me, accessToken, refreshToken }));
      }
      navigate('/onboarding');
    } catch (err) {
      setSubmitError((err as Error).message ?? 'Could not create your account.');
    }
  };

  const sendMagicLink = async () => {
    if (!existingAccountEmail) return;
    try {
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: existingAccountEmail }),
      });
      if (!res.ok) throw new Error(`Could not send magic link (${res.status})`);
      setMagicLinkSent(true);
    } catch (err) {
      setSubmitError((err as Error).message);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <AuthBrandSide
        title={
          <>
            Ship emails that <i style={{ color: 'var(--flame-2)' }}>just work</i>.
          </>
        }
        sub="EmailOps for developers. A typed SDK, an editor for product teams, and a sending pipeline that survives launch day. Free up to 3 templates."
        testimonial={{
          q: 'We replaced a Mailchimp flow and three React Email files with Emailforge in a weekend. Audit log + deliverability dashboards in one place — finally.',
          n: 'Marina Díaz',
          t: 'Founder · Arcoiris Agency',
          initials: 'MD',
          color: 'oklch(0.65 0.15 65)',
        }}
      />

      <div
        style={{
          flex: 1,
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>
          {existingAccountEmail ? (
            <ExistingAccountPanel
              email={existingAccountEmail}
              sent={magicLinkSent}
              onSendLink={sendMagicLink}
              onTryDifferentEmail={() => {
                setExistingAccountEmail(null);
                setMagicLinkSent(false);
              }}
            />
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h1 className="display" style={{ fontSize: 34, margin: '0 0 8px', letterSpacing: -0.02 }}>
                  Create your workspace
                </h1>
                <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: 0 }}>
                  Already have an account?{' '}
                  <Link to="/login" style={{ color: 'var(--flame-2)', textDecoration: 'none', fontWeight: 500 }}>
                    Sign in
                  </Link>
                </p>
              </div>

              <OAuthButtons />

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0' }}>
                <div className="mf-div" style={{ flex: 1 }} />
                <span
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, textTransform: 'uppercase' }}
                >
                  or with email
                </span>
                <div className="mf-div" style={{ flex: 1 }} />
              </div>

              <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <Field label="Email" error={errors.email?.message}>
                  <Input
                    type="email"
                    style={{ height: 38 }}
                    placeholder="founder@acme.inc"
                    autoComplete="email"
                    {...register('email')}
                  />
                </Field>

                {showPasswordField ? (
                  <Field label="Password" error={errors.password?.message}>
                    <Input
                      type="password"
                      style={{ height: 38 }}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      {...register('password')}
                    />
                    {password && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            style={{
                              flex: 1,
                              height: 3,
                              borderRadius: 2,
                              background: i < strength ? 'var(--flame)' : 'var(--paper-4)',
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </Field>
                ) : (
                  <div
                    style={{
                      padding: '12px 14px',
                      background: 'var(--flame-soft)',
                      border: '1px solid var(--flame-line)',
                      borderRadius: 'var(--r-3)',
                      fontSize: 12.5,
                      color: 'var(--flame-2)',
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Icon.bolt size={16} />
                    <span style={{ flex: 1, lineHeight: 1.5 }}>
                      <strong>No password needed.</strong> We'll send a magic link the first time you sign in.{' '}
                      <button
                        type="button"
                        onClick={() => setShowPasswordField(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: 'var(--flame-2)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: 12.5,
                          fontWeight: 500,
                        }}
                      >
                        Set a password instead
                      </button>
                    </span>
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--ink-2)' }}>
                  <input type="checkbox" {...register('acceptTerms')} style={{ marginTop: 2 }} />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" style={{ color: 'var(--flame-2)' }}>
                      Terms
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank" style={{ color: 'var(--flame-2)' }}>
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                {errors.acceptTerms && (
                  <span style={{ fontSize: 12, color: 'var(--err)' }}>{errors.acceptTerms.message}</span>
                )}

                {submitError && (
                  <div
                    style={{
                      background: 'var(--err-soft)',
                      color: 'var(--err)',
                      padding: '10px 12px',
                      borderRadius: 'var(--r-2)',
                      fontSize: 12.5,
                      border: '1px solid oklch(0.85 0.10 25)',
                    }}
                  >
                    {submitError}
                  </div>
                )}

                <Button
                  variant="flame"
                  style={{ height: 42, justifyContent: 'center', marginTop: 6 }}
                  disabled={isSubmitting}
                  type="submit"
                >
                  {isSubmitting ? 'Creating…' : 'Create workspace'}
                  <Icon.arrow size={14} />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ExistingAccountPanel({
  email,
  sent,
  onSendLink,
  onTryDifferentEmail,
}: {
  email: string;
  sent: boolean;
  onSendLink: () => void;
  onTryDifferentEmail: () => void;
}) {
  if (sent) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--r-3)',
            background: 'var(--spark-soft)',
            border: '1px solid var(--spark-line)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--spark)',
            marginBottom: 20,
          }}
        >
          <Icon.check size={22} />
        </div>
        <h1 className="display" style={{ fontSize: 28, margin: '0 0 10px' }}>
          Check your inbox
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
          We sent a sign-in link to <strong>{email}</strong>. Click it to enter your workspace —
          it works for 15 minutes.
        </p>
        <Button onClick={onTryDifferentEmail} style={{ marginTop: 20 }}>
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="display" style={{ fontSize: 28, margin: '0 0 10px', letterSpacing: -0.02 }}>
          You already have a workspace
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: 0, lineHeight: 1.55 }}>
          An account exists for <strong>{email}</strong>. Send yourself a magic link to sign back in.
        </p>
      </div>

      <Button
        variant="flame"
        style={{ width: '100%', height: 42, justifyContent: 'center' }}
        onClick={onSendLink}
      >
        Email me a sign-in link
        <Icon.arrow size={14} />
      </Button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0' }}>
        <div className="mf-div" style={{ flex: 1 }} />
        <span
          className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, textTransform: 'uppercase' }}
        >
          or
        </span>
        <div className="mf-div" style={{ flex: 1 }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Link
          to="/login"
          style={{
            textAlign: 'center',
            padding: '10px',
            fontSize: 13,
            color: 'var(--ink-2)',
            textDecoration: 'none',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-2)',
          }}
        >
          Sign in with password
        </Link>
        <button
          type="button"
          onClick={onTryDifferentEmail}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 12.5,
            color: 'var(--ink-3)',
            cursor: 'pointer',
            textAlign: 'center',
            padding: '8px',
          }}
        >
          ← Use a different email
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
      {error && <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function passwordStrength(pw: string): number {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
